<template>
  <v-container text-xs-center>
    <v-layout
      row
      wrap
      style="align-items: center"
    >
      <v-btn
        flat
        class="px-0 mx-0 text-capitalize back-btn"
        @click="goBack()"
      >
        <v-icon class="mr-2">
          $vuetify.icons.arrowLeftIcon
        </v-icon>
        Voltar
      </v-btn>
    </v-layout>

    <Loader
      v-if="isLoading"
      class="mt-5"
    />

    <v-card
      v-else-if="!isLoading && product && store"
      flat
      class="mx-auto animated fadeIn delay-1s px-2 pb-5 mb-3"
    >
      <ProductInfos
        :product="product"
        :store="store"
        @change-quantity="changeQuantityZeroStock"
      />

      <v-dialog
        v-if="warrantyMobileDialog"
        v-model="warrantyMobileDialog"
        max-width="330"
      >
        <WarrantyMobileDialog
          :product="product"
          :quantity="quantity"
          @change-quantity-warranty="changeQuantityWarranty"
          @finish-warranty="finishWarranty"
          @close="() => { warrantyMobileDialog = false }"
        />
      </v-dialog>

      <v-dialog
        v-if="warrantyDialog"
        v-model="warrantyDialog"
        max-width="330"
      >
        <WarrantyDialog
          :product="product"
          :quantity="quantity"
          @change-quantity-warranty="changeQuantityWarranty"
          @finish-warranty="finishWarranty"
          @close="() => { warrantyDialog = false }"
        />
      </v-dialog>

      <v-dialog
        v-if="servicesDialog"
        v-model="servicesDialog"
        persistent
        max-width="330"
      >
        <ServicesDialog
          :product="product"
          :quantity="quantity"
          @finish-services="finishWarranty"
          @change-quantity-services="changeQuantityWarranty"
          @close="() => { servicesDialog = false }"
        />
      </v-dialog>

      <v-dialog
        v-if="zeroStockDialog"
        v-model="zeroStockDialog"
        persistent
        max-width="360"
      >
        <ZeroStockDialog
          :close="() => { zeroStockDialog = false }"
          :add-to-cart="addToCartAndRedirect"
          :product-selected="product"
          :has-stock="currentStock.stock > 0"
          :store-stock-quantity="storeStockQuantity"
          :desired-amount="quantity"
          @change-quantity="changeQuantityZeroStock"
        />
      </v-dialog>

      <template v-if="getCustomer.salesAssistant">
        <v-card-actions class="justify-center mx-0 mt-2">
          <div class="product-quantity-selector">
            <v-btn
              flat
              icon
              color="rgba(0, 0, 0, 0.36)"
              style="font-size: 20px;"
              @click="changeProductQuantity('reduce')"
            >
              -
            </v-btn>
            <div>
              {{ quantity }}
            </div>
            <v-btn
              flat
              icon
              color="rgba(0, 0, 0, 0.36)"
              style="font-size: 20px;"
              @click="changeProductQuantity('increase')"
            >
              +
            </v-btn>
          </div>

          <v-btn
            slot="activator"
            block
            color="primary"
            class="button primary-btn"
            style="align-items: center"
            @click="openIdentifyOrWarrantyDialog"
          >
            Adicionar ao carrinho
          </v-btn>
        </v-card-actions>
      </template>

      <template v-else>
        <v-card-actions class="justify-center mx-0 mt-2">
          <v-btn
            slot="activator"
            block
            color="#FFADDE"
            class="primary-btn"
            style="align-items: center"
            @click="addToCartAndRedirect(false, '/personalizar')"
          >
            <v-icon
              left
            >
              $vuetify.icons.cartIcon
            </v-icon>
            Adicionar
          </v-btn>
        </v-card-actions>

        <v-card-actions class="justify-center mx-0 pt-0">
          <v-btn
            slot="activator"
            block
            color="primary"
            class="button primary-btn"
            style="align-items: center"
            @click="addToCartAndRedirect(false, '/catalogo')"
          >
            Adicionar e ir ao carrinho
          </v-btn>
        </v-card-actions>
      </template>

      <v-dialog
        v-model="identifyPersonDialog"
        max-width="379"
      >
        <IdentifyPersonDialog
          :go-to-personalize="() => addToCartAndRedirect(true, '/personalizar')"
          :close="() => { identifyPersonDialog = false }"
        />
      </v-dialog>

      <RecommendedProduct
        v-if="recommendedProducts.length"
        :recommended-products="recommendedProducts"
      />
    </v-card>
  </v-container>
</template>

<script>
/* eslint-disable import/no-unresolved */
import { mapGetters } from 'vuex';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { APIService } from '@/resources/campanha.js';
// eslint-disable-next-line import/no-unresolved, import/extensions
import { EventBus } from '@/event-bus.js';
import ProductInfos from '@/components/Product/ProductInfos';
import RecommendedProduct from '@/components/Product/RecommendedProduct';
import { everything } from '@sejazipper/layer-api-client';
import { useZipperApi } from '../composables/useZipperApi';
import WarrantyDialog from '../components/Product/WarrantyDialog.vue';
import WarrantyMobileDialog from '../components/Product/WarrantyMobileDialog.vue';
import ServicesDialog from '../components/Product/ServicesDialog.vue';
import ZeroStockDialog from '../components/Product/ZeroStockDialog.vue';
import Loader from '../components/Loader.vue';

const api = new APIService();
const { client } = useZipperApi();

export default {
  components: {
    ProductInfos,
    RecommendedProduct,
    WarrantyDialog,
    WarrantyMobileDialog,
    ServicesDialog,
    ZeroStockDialog,
    Loader,
  },

  data: () => ({
    product: null,
    isLoading: false,
    recommendedProducts: [],
    selectedSku: {},
    warrantyDialog: false,
    servicesDialog: false,
    identifyPersonDialog: false,
    zeroStockDialog: false,
    warrantyMobileDialog: false,
    redirectPath: null,
    quantity: 1,
    discountPrice: 0,
    unitiesToApply: 0,
    store: undefined,
    storeStockQuantity: 0,
  }),

  computed: {
    ...mapGetters(['getStore', 'getCustomer', 'getPerson', 'personIsIdentified', 'getProfile', 'getSelectedProductColor']),

    hasServices() {
      return this.product.skus[0].sku_add_ons.find((skuAddOn) => skuAddOn.type === 'PS' && skuAddOn.store_id === this.store.id);
    },

    hasWarranty() {
      return this.product.skus[0].sku_add_ons.find((skuAddOn) => skuAddOn.type === 'GE' && skuAddOn.store_id === this.store.id);
    },

    hasWarrantyMobile() {
      return this.product.skus[0].sku_add_ons.find((skuAddOn) => skuAddOn.type === 'SM' && skuAddOn.store_id === this.store.id);
    },

    currentStock() {
      if (this.product && this.product.store_id) {
        return (this.selectedSku.sku_stocks && this.store) && this.selectedSku.sku_stocks.find((stock) => stock.store_id === this.product.store_id);
      }

      return (this.selectedSku.sku_stocks && this.store) && this.selectedSku.sku_stocks.find((stock) => stock.store_id === this.store.id);
    },

    currentPrice() {
      if (this.product && this.product.store_id) {
        return (this.selectedSku.sku_prices && this.store) && this.selectedSku.sku_prices.find((price) => price.store_id === this.product.store_id);
      }

      return (this.selectedSku.sku_prices && this.store) && this.selectedSku.sku_prices.find((price) => price.store_id === this.store.id);
    },

    getQueryParams() {
      return this.$route.params.id;
    },
  },

  watch: {
    async getStore(store) {
      if (store) {
        await this.getProduct();
        this.store = await client.chain.query.findStoreByMongoId({
          mongoId: store.id,
        }).get({ ...everything });
      }
    },

    product: {
      handler(product) {
        if (!this.getCustomer.salesAssistant) return;

        this.selectedSku = product.skus.find((sku) => (
          (!product.size || product.size === sku.size)
            && (!product.color || product.color === sku.color)
        ));
      },
      deep: true,
    },

    currentStock() {
      if (!this.currentStock || (this.currentStock && this.currentStock.stock === 0)) {
        this.quantity = 0;
      } else {
        this.quantity = 1;
      }
    },

    async getQueryParams() {
      await this.getProduct();
    },
  },

  async mounted() {
    this.store = await client.chain.query.findStoreByMongoId({
      mongoId: this.getStore.id,
    }).get({ ...everything });

    await this.getProduct();

    if (this.currentStock && this.currentStock.stock === 0) {
      this.quantity = 0;
    }

    EventBus.$on('addDiscount', (discountPrice) => {
      this.discountPrice = discountPrice;
    });
  },

  methods: {
    async setMainImage(product, image) {
      this.product.mainImage = image;
      if (this.getCustomer.id === '5e8fb3bc454d620004792371') {
        const adidas = ['5f637f1e5e9f1600030a6319', '5f637eac5e9f1600030a62b7'];
        adidas.forEach(async (id) => {
          await api.setProductMainImage(product, image, id);
        });
      }
      await api.setProductMainImage(product, image);
      await this.getProduct();
    },

    async getProduct() {
      try {
        this.product = null;
        this.isLoading = true;
        const { skuId } = this.$route.query;
        const res = await client.chain.query.findOMSProductById({
          productId: Number(this.$route.params.id),
        }).get({
          id: true,
          setor: true,
          grupoProduto: true,
          descricaoCurta: true,
          descricao: true,
          categoria: true,
          hashCode: true,
          dataInclusao: true,
          dataAlteracao: true,
          skus: {
            sku_id_erp: true,
            sku_add_ons: true,
            sku_images: true,
            sku_datasheets: true,
            sku_prices: true,
            sku_stocks: true,
            ...everything,
          },
        });

        this.product = {
          ...res,
          price: undefined,
          oldPrice: undefined,
          store_id: null,
        };

        this.product.skus = this.product.skus.filter((sku) => sku.sku_prices.length);

        const skuSelected = this.product.skus.find((sku) => sku.id === Number(skuId));

        if (skuSelected) {
          const stockSelected = skuSelected.sku_stocks.find((stock) => stock.store.mongo_id === this.getStore.id);

          if (stockSelected) {
            this.product.store_id = stockSelected.store.id;
          }
        }
        if (skuSelected && skuSelected.product_type_id !== 4) {
          this.product.skus = this.product.skus.filter((sku) => sku.id === Number(skuId));
        } else {
          this.product.skus = this.product.skus.filter((sku) => sku.product_type_id === 4);
        }

        this.isLoading = false;
        this.getRecommendProducts(res.grupoProduto, skuId);
      } catch (e) {
        console.log(e);
      }
    },

    async getRecommendProducts(category, skuId) {
      this.recommendedProducts = await client.chain.query.recommendedProductsByCategory({
        category,
        skuId: Number(skuId),
        storeId: this.getStore.id,
      }).get({
        id: true,
        setor: true,
        grupoProduto: true,
        descricaoCurta: true,
        descricao: true,
        categoria: true,
        hashCode: true,
        dataInclusao: true,
        dataAlteracao: true,
        skus: {
          sku_id_erp: true,
          sku_add_ons: true,
          sku_images: true,
          sku_datasheets: true,
          sku_prices: true,
          sku_stocks: true,
          ...everything,
        },
      });
    },

    emitSnackbar() {
      const snackBar = {};
      snackBar.value = true;
      snackBar.text = 'Adicionado ao catálogo com sucesso';
      snackBar.color = '#140C22';
      snackBar.button = false;
      snackBar.icon = '$vuetify.icons.checkIcon';
      snackBar.class = {
        height: '120px',
        'font-size': '16px',
        'font-weight': '600',
        'justify-content': 'center',
      };
      EventBus.$emit('activeSnackBar', snackBar);
    },

    changeProductQuantity(operation) {
      const values = {
        reduce: this.quantity - 1,
        increase: this.quantity + 1,
      };

      if (values[operation] < 1) {
        return;
      }

      const { isCD } = this.product;
      const stockCD = isCD ? this.selectedSku.sku_stocks.find((skuStock) => skuStock.store.id_erp === '52') : null;

      if (isCD && operation === 'increase' && this.quantity >= stockCD.stock) {
        EventBus.$emit('activeSnackBar', { text: 'Limite de estoque atingido', value: true, color: 'red' });
      } else {
        this.quantity = isCD ? values[operation] : values[operation];
      }
    },

    openIdentifyOrWarrantyDialog() {
      if (!this.personIsIdentified) {
        this.identifyPersonDialog = true;
        return;
      }

      this.addToCartAndRedirect(true, '/personalizar');
    },

    addToCatalogue() {
      let { product } = this;

      if (this.getCustomer.salesAssistant) {
        product = {
          ...product,
          ean: this.selectedSku.ean,
          size: this.selectedSku.size,
          color: this.selectedSku.color,
          reference: this.selectedSku.reference,
          sku_id: this.selectedSku.id,
          skuId: `${this.selectedSku.size || ''}${this.selectedSku.color || ''}`,
          old_price: this.currentPrice ? this.currentPrice.old_price : 0,
          price: this.currentPrice ? this.currentPrice.price : 0,
          currentStock: this.currentStock ? this.currentStock.stock : 0,
          quantity: this.quantity,
        };

        if (this.discountPrice) {
          product.oldPrice = this.currentPrice.price;
          product.price = this.discountPrice;
          product.hasDiscount = true;
        }
      }

      if (this.unitiesToApply > 0 && (product.warranty || product.warrantyMobile || (product.services && product.services.length))) {
        product = this.splitProducts(product);
      }

      if (product.length) {
        product.forEach((item) => {
          this.$store.dispatch('addToCatalogue', item);
        });
      } else {
        this.$store.dispatch('addToCatalogue', product);
      }

      this.emitSnackbar();
    },

    addToCartAndRedirect(hasSalesAssistant, redirectPath) {
      this.identifyPersonDialog = false;
      if (redirectPath) this.redirectPath = redirectPath;

      if (hasSalesAssistant) {
        const zerostock = this.currentStock.stock < 1;

        if ((zerostock || this.quantity > this.currentStock.stock) && !this.product.isCD) {
          this.storeStockQuantity = this.currentStock ? this.currentStock.stock : 0;
          this.zeroStockDialog = true;
          return;
        }

        if ((!this.getPerson.type && this.getPerson.cpf) || this.getPerson.type === 'PF') {
          if (this.hasWarranty || this.hasWarrantyMobile) {
            // eslint-disable-next-line no-unused-expressions
            this.product.grupoProduto === 'TELEFONIA' ? this.warrantyMobileDialog = true : this.warrantyDialog = true;
          } else if (this.hasServices) {
            this.servicesDialog = true;
          } else {
            this.finishWarranty();
            this.$store.dispatch('refreshCatalogue');
          }

          return;
        }
      }

      this.addToCatalogue();
      this.$router.replace(this.redirectPath);
    },

    goBack() {
      window.history.back();
    },

    finishWarranty() {
      this.addToCartAndRedirect(false, '/personalizar');
    },

    updateProductAttributeByKey(key, value) {
      this.product[key] = value;
    },

    changeQuantityWarranty(quantity) {
      this.unitiesToApply = quantity;
    },

    splitProducts(product) {
      const item = { ...product };

      delete item.warranty;
      delete item.warrantyMobile;
      delete item.services;

      const newProducts = [];

      if (product.imeis && product.imeis.length) {
        product.imeis.forEach((imei) => newProducts.push({
          ...product,
          services: [],
          quantity: 1,
          imei,
        }));

        if (item.quantity !== product.imeis.length) {
          newProducts.push({
            ...item,
            services: [],
            quantity: item.quantity - product.imeis.length,
            imeis: undefined,
          });
        }
      } else {
        if (product.services && product.services.length) {
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < this.unitiesToApply; i++) {
            newProducts.push({
              ...product,
              quantity: 1,
              imeis: undefined,
              services: [...product.services],
              warranty: product.warranty || undefined,
              warrantyMobile: product.warrantyMobile || undefined,
            });
          }
        } else {
          newProducts.push({
            ...product,
            quantity: this.unitiesToApply,
            imeis: undefined,
            services: [],
            warranty: product.warranty || undefined,
            warrantyMobile: product.warrantyMobile || undefined,
          });
        }

        if ((item.quantity - this.unitiesToApply) > 0) {
          newProducts.push({
            ...item,
            quantity: item.quantity - this.unitiesToApply,
            services: [],
            warranty: undefined,
            warrantyMobile: undefined,
          });
        }
      }

      return newProducts;
    },

    changeQuantityZeroStock(quantity) {
      this.quantity = quantity;
    },
  },
};
</script>

<style scoped lang="scss">
body {
  background: #FDFCFB;
}

.product-quantity-selector {
  display: flex;
  justify-content: space-around;
  align-items: center;
  font-family: 'Roboto';
  font-weight: 500;
  font-size: 20px;
  color: #140C22;
  width: 30vw;
  height: 46px;
  border: 2px solid rgba(0, 0, 0, 0.06);
  border-radius: 7px;
  margin-right: 12px;
}

.primary-btn {
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 600;
  font-size: min(4.5vw, 16px);
  line-height: 22px;
  color: #140C22;
  border-radius: 7px !important;
  height: 46px;
  text-transform: none;
}

.back-btn {
  font-family: 'Neue-Metana';
  color: #140C22;
  font-weight: 400;
  font-size: 14px;
  border-radius: 45%;
}
</style>
